import React from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * Admin-only shell.
 * Frontend guard is a CONVENIENCE — the backend re-verifies role on every
 * /api/admin/* endpoint. Do not rely on this component for security.
 */
export default function AdminShell() {
  const { user, isChecking } = useAuth();
  const { t, lang } = useI18n();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("common.loading")}
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.role !== "administrator") return <Navigate to="/app/discover" replace />;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-shell">
      <header
        className="border-b sticky top-0 z-30"
        style={{
          borderColor: "hsl(var(--nvl-border))",
          backgroundColor: "hsl(var(--nvl-surface))",
        }}
      >
        <div className="nvl-container py-4 flex flex-wrap items-center gap-4">
          <Link to="/admin/moderation" className="font-display text-lg tracking-tight flex items-center gap-2">
            <ShieldCheck size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
            NO VAX LOVE — Admin
          </Link>
          <nav className="flex-1 flex flex-wrap items-center gap-4 text-sm">
            <Link to="/admin/message-withdrawals">{lang === "it" ? "Ritiro messaggi" : "Message withdrawals"}</Link>
            {process.env.REACT_APP_RESIDUAL_REVIEWS_ENABLED === "1" && <>
              <Link to="/admin/inactivity-reviews" className="underline-offset-4 hover:underline">{lang === "it" ? "Pratiche esterne" : "External cases"}</Link>
              <Link to="/admin/retention" className="underline-offset-4 hover:underline">
                {lang === "en" ? "Deletion reviews" : "Cancellazioni"}
              </Link></>}
            <Link
              to="/admin/moderation"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-moderation"
            >
              {t("app.admin.nav.moderation")}
            </Link>
            <Link
              to="/admin/audit-log"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-audit"
            >
              {t("app.admin.nav.auditLog")}
            </Link>
            <Link
              to="/admin/billing-config"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-billing"
            >
              {t("app.admin.nav.billing")}
            </Link>
          </nav>
          <LanguageSwitcher compact />
          <Link
            to="/app/discover"
            className="text-xs underline"
            style={{ color: "hsl(var(--nvl-ink-soft))" }}
            data-testid="admin-back-to-app"
          >
            ← App
          </Link>
        </div>
      </header>
      <main className="flex-1 nvl-container py-8 md:py-12">
        <Outlet />
      </main>
    </div>
  );
}
